import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Inbox, Clock, CheckCircle2, ArrowRightLeft, XCircle, ChevronRight, ChevronDown, Package, Boxes,
} from "lucide-react";
import { cn } from "../../utils/tailwindUtils";
import ToastMsg from "../../components/common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";
import { ASSET_REQUEST } from "../../constants/api";
import {
  ASSET_REQUEST_TYPES, ASSET_REQUEST_STATUSES, shortType,
} from "../../constants/assetRequest";
import { StatusBadge, EmptyState, fmtDate } from "../assetmanagement/AssetRequestPieces";

/* Self-contained asset-request overview (stat cards + by-type/by-status bars +
   recent), in a collapsible card like DayEndApprovalCard. Auto-expands whenever
   the user has any OPEN request (Pending / Approved / Issued). Shared by the
   standalone admin page and the Home / AdminDashboard screens. */

const STATUS_COLOR = {
  Pending: "#d97706", Approved: "#8b5cf6", Issued: "#16a34a", Rejected: "#dc2626", Returned: "#0ea5e9",
};

// Status → compact-summary pill palette.
const PILL = {
  Total: "bg-slate-50 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700",
  Pending: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800",
  Approved: "bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-900/20 dark:text-violet-300 dark:border-violet-800",
  Issued: "bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-300 dark:border-green-800",
  Rejected: "bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800",
};

const AssetDashboardSection = () => {
  const navigate = useNavigate();
  const { fetch } = useApiMethods();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(ASSET_REQUEST);
      // Backend paginates: data = { result: [...], total_count, ... }.
      if (res?.status === "success") {
        setRequests(Array.isArray(res.data) ? res.data : (res.data?.result || []));
      }
    } catch (e) {
      ToastMsg("error", e.message);
    } finally {
      setLoading(false);
    }
    // `fetch` is a new ref every render — keep it out of deps to avoid a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const count = useCallback((s) => requests.filter((r) => r.status === s).length, [requests]);

  // "Open" = anything not yet closed (Rejected/Returned).
  const openCount = useMemo(
    () => requests.filter((r) => ["Pending", "Approved", "Issued"].includes(r.status)).length,
    [requests]
  );

  // Auto-expand when the user currently has any open request; collapse otherwise.
  useEffect(() => { setExpanded(openCount > 0); }, [openCount]);

  const cards = useMemo(() => ([
    { label: "Total Requests", v: requests.length, Icon: Inbox, color: "#6366f1" },
    { label: "Pending", v: count("Pending"), Icon: Clock, color: "#d97706" },
    { label: "Approved", v: count("Approved"), Icon: CheckCircle2, color: "#8b5cf6" },
    { label: "Issued", v: count("Issued"), Icon: ArrowRightLeft, color: "#16a34a" },
    { label: "Rejected", v: count("Rejected"), Icon: XCircle, color: "#dc2626" },
  ]), [requests, count]);

  const summaryPills = useMemo(() => ([
    ["Total", requests.length],
    ["Pending", count("Pending")],
    ["Approved", count("Approved")],
    ["Issued", count("Issued")],
    ["Rejected", count("Rejected")],
  ]), [requests, count]);

  const byType = useMemo(
    () => ASSET_REQUEST_TYPES.map((t) => ({ label: shortType(t), v: requests.filter((r) => r.request_type === t).length })),
    [requests]
  );
  const byStatus = useMemo(
    () => ASSET_REQUEST_STATUSES.map((s) => ({ label: s, v: count(s), color: STATUS_COLOR[s] })).filter((d) => d.v),
    [requests, count]
  );
  const recent = useMemo(
    () => [...requests].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 5),
    [requests]
  );

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Header (click to toggle) */}
      <div className="p-5 cursor-pointer" onClick={() => setExpanded((p) => !p)}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Boxes className="w-5 h-5 text-sidebar-primary" />
            <div>
              <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-200">
                Asset Requests
              </h2>
              <p className="text-xs text-muted-foreground mt-1">
                {openCount > 0
                  ? `${openCount} open request${openCount > 1 ? "s" : ""}`
                  : "Asset request overview"}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <button
              onClick={(e) => { e.stopPropagation(); navigate("/assetmanagement/assetrequest"); }}
              className="text-xs font-medium text-sidebar-primary hover:underline flex items-center gap-1"
            >
              View all <ChevronRight className="w-3 h-3" />
            </button>
            <ChevronDown className={cn("w-4 h-4 transition-transform duration-300", expanded && "rotate-180")} />
          </div>
        </div>

        {/* Compact summary (collapsed) */}
        {!expanded && (
          <div className="mt-5 flex flex-wrap gap-2">
            {summaryPills.map(([label, value]) => (
              <span
                key={label}
                className={cn("inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium", PILL[label])}
              >
                {label} <span className="font-bold">{value}</span>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Expanded content */}
      {expanded && (
        <div className="border-t border-gray-200 dark:border-gray-700 p-5 space-y-3">
          {/* stat cards */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            {cards.map((c) => (
              <div key={c.label} className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4">
                <span className="mb-2.5 grid h-9 w-9 place-items-center rounded-lg"
                  style={{ background: c.color + "1a", color: c.color }}>
                  <c.Icon className="h-4 w-4" />
                </span>
                <div className="text-2xl font-bold leading-none">{c.v}</div>
                <div className="mt-1 text-xs text-muted-foreground">{c.label}</div>
              </div>
            ))}
          </div>

          {/* charts */}
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            <Panel title="Requests by Type">
              <BarChart data={byType} />
            </Panel>
            <Panel title="Requests by Status">
              {byStatus.length === 0 ? <EmptyState label="No requests yet." /> : <BarChart data={byStatus} />}
            </Panel>
          </div>

          {/* recent */}
          <Panel title="Recent Requests">
            {loading ? (
              <EmptyState label="Loading…" />
            ) : recent.length === 0 ? (
              <EmptyState label="No requests yet." />
            ) : (
              <div className="flex flex-col gap-2">
                {recent.map((r) => (
                  <button key={r.id} onClick={() => navigate(`/assetmanagement/assetrequest/${r.id}`)}
                    className="flex items-center gap-3 rounded-xl border border-gray-200 dark:border-gray-700 p-3 text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/60">
                    <span className="grid h-9 w-9 place-items-center rounded-lg bg-sidebar-primary/10 text-sidebar-primary">
                      <Package className="h-4 w-4" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium">
                        {r.requester_name} · <span className="text-muted-foreground">{r.category_name}</span>
                      </span>
                      <span className="block text-xs text-muted-foreground">
                        {r.request_no} · {r.department} · {fmtDate(r.created_at)}
                      </span>
                    </span>
                    <StatusBadge status={r.status} />
                  </button>
                ))}
              </div>
            )}
          </Panel>
        </div>
      )}
    </div>
  );
};

const Panel = ({ title, children }) => (
  <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5">
    <h3 className="mb-4 text-base font-semibold">{title}</h3>
    {children}
  </div>
);

const BarChart = ({ data }) => {
  const max = Math.max(...data.map((d) => d.v), 1);
  return (
    <div className="flex flex-col gap-2.5">
      {data.map((d) => (
        <div key={d.label} className="flex items-center gap-3">
          <span className="w-24 shrink-0 text-right text-xs text-muted-foreground">{d.label}</span>
          <div className="h-5 flex-1 overflow-hidden rounded-md bg-gray-100 dark:bg-gray-800">
            <div className="h-full rounded-md transition-all"
              style={{ width: `${(d.v / max) * 100}%`, background: d.color || "#0a9690" }} />
          </div>
          <span className="w-6 text-xs font-bold text-gray-700 dark:text-gray-200">{d.v}</span>
        </div>
      ))}
    </div>
  );
};

export default AssetDashboardSection;
